import { Injectable, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { SettingsService, _HttpClient } from '@delon/theme';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { AbstractControl, Validators } from '@angular/forms';

@Injectable({
  providedIn: 'root'
})
export class UserDefaultService {
  private _disableSaveBtn: boolean = false;
  public get disableSaveBtn(): boolean {
    return this._disableSaveBtn;
  }

  private _selectedUserId?: string;
  public get selectedUser(): string | undefined {
    return this._selectedUserId;
  }
  tempSelectedUser: string = '';

  public loading = false;

  private usersSubject = new BehaviorSubject<Array<{
    name: string;
    id: string;
  }> | null>(null);
  public users$ = this.usersSubject.asObservable();
  public usersLoading = false;

  // Debug toggle for setControlRequired function - set to false to disable logging
  private readonly DEBUG_REQUIRED_FIELDS = true;

  // In-memory cache for default settings, keyed by userId
  private defaultSettingsCache: Map<string, { [key: string]: any }> = new Map();

  private readonly userDefaultSettingsListRefresh$ = new Subject<void>();

  /**
   * Subscribe on the User Default Settings list screen to refetch the table after
   * settings are saved on the detail screen (list may be a reused tab).
   */
  readonly userDefaultSettingsListRefresh =
    this.userDefaultSettingsListRefresh$.asObservable();

  requestUserDefaultSettingsListRefresh(): void {
    this.userDefaultSettingsListRefresh$.next();
  }

  constructor(
    private http: _HttpClient,
    public settings: SettingsService,
    public router: Router
  ) {}

  /**
   * Get default settings from in-memory cache for the current user.
   * Returns an Observable that emits the settings when available.
   * If cache exists, emits immediately. If not, loads settings first, then emits.
   */
  getDefaultSettings(): Observable<{ [key: string]: any } | null> {
    // Get current user ID from SettingsService
    let curUserId: string | null = null;

    if (this.settings?.user?.['userId']) {
      curUserId = this.settings.user['userId'];
    }

    if (!curUserId) {
      if (this.DEBUG_REQUIRED_FIELDS) {
        console.warn(
          'getDefaultSettings: No current user found, returning null.',
          {
            hasSettingsService: !!this.settings
          }
        );
      }
      return of(null);
    }

    // Check if settings exist in cache
    const userSettings = this.defaultSettingsCache.get(curUserId) ?? null;

    if (userSettings) {
      // Cache exists, emit immediately
      return of(userSettings);
    }

    // Cache doesn't exist, load settings first
    if (this.DEBUG_REQUIRED_FIELDS) {
      console.log(
        `getDefaultSettings: No settings found for userId: "${curUserId}", loading...`,
        {
          availableUserIds: Array.from(this.defaultSettingsCache.keys())
        }
      );
    }

    // Load settings and return Observable that emits when complete
    return this.http
      .get('/api/userdefaultsettings/form', { uid: curUserId })
      .pipe(
        switchMap((formData: any) => {
          // Backend now returns data directly in form structure
          // Convert to lowercase format for cache compatibility
          const lowerCaseFormValue: any = {};
          Object.keys(formData).forEach(key => {
            if (key === 'userName') {
              lowerCaseFormValue.username = formData[key];
            } else {
              // Convert field name to lowercase and preserve field structure
              const lowerKey = key.toLowerCase();
              lowerCaseFormValue[lowerKey] = {
                ...formData[key],
                // Add defaultItem alias for backward compatibility
                defaultItem: formData[key]?.default ?? null,
                // Add manageSelection alias for backward compatibility (using selectedItemIds)
                manageSelection: formData[key]?.selectedItemIds ?? []
              };
            }
          });

          this.updateDefaultSettingsCache(lowerCaseFormValue);

          const cachedSettings =
            this.defaultSettingsCache.get(curUserId!) ?? null;
          return of(cachedSettings);
        })
      );
  }

  getDefaultSupplierCategory(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const supplierCategorySettings = defaultSettings?.['suppliercategory'];
        return of(supplierCategorySettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultStockArea(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        // Get the area defaultItem from cached settings
        const areaSettings = defaultSettings?.['area'];
        return of(areaSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultCustomerCategory(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const customerCategorySettings = defaultSettings?.['customercategory'];
        return of(customerCategorySettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultStockCategory(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const stockCategorySettings = defaultSettings?.['stockcategory'];
        return of(stockCategorySettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultStockClass(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const stockClassSettings = defaultSettings?.['stockclass'];
        return of(stockClassSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultStockGroup(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const stockGroupSettings = defaultSettings?.['stockgroup'];
        return of(stockGroupSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultDeposit(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const depositSettings = defaultSettings?.['deposit'];
        return of(depositSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultPayment(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const paymentSettings = defaultSettings?.['paymentby'];
        return of(paymentSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultAgent(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const agentSettings = defaultSettings?.['agent'];
        return of(agentSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultPurchaser(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const purchaserSettings = defaultSettings?.['purchaser'];
        return of(purchaserSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultLocation(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const locationSettings = defaultSettings?.['location'];
        return of(locationSettings?.defaultItem ?? null);
      })
    );
  }

  getDefaultProject(): Observable<any | null> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const projectSettings = defaultSettings?.['project'];
        return of(projectSettings?.defaultItem ?? null);
      })
    );
  }

  isLocationRestricted(): Observable<boolean> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const locationSettings = defaultSettings?.['location'];
        return of(
          locationSettings?.isRestrict === true &&
            locationSettings?.manageSelection?.length > 0
        );
      })
    );
  }

  isProjectRestricted(): Observable<boolean> {
    return this.getDefaultSettings().pipe(
      switchMap(defaultSettings => {
        const projectSettings = defaultSettings?.['project'];
        return of(
          projectSettings?.isRestrict === true &&
            projectSettings?.manageSelection?.length > 0
        );
      })
    );
  }

  setControlRequired(
    control: AbstractControl,
    settingName: string,
    element?: ElementRef<HTMLDivElement>
  ) {
    if (this.DEBUG_REQUIRED_FIELDS) {
      // Get caller information from stack trace
      const stack = new Error().stack;
      const callerMatch = stack?.split('\n')[3]?.trim();
      const callerInfo = callerMatch || 'unknown';

      console.log(`[setControlRequired] Processing field: "${settingName}"`, {
        calledFrom: callerInfo,
        timestamp: new Date().toISOString()
      });
    }

    if (!control) {
      if (this.DEBUG_REQUIRED_FIELDS) {
        console.warn(
          `[setControlRequired] Control is null for field: "${settingName}"`
        );
      }
      return;
    }

    // Get current user ID and check cache synchronously
    let curUserId: string | null = null;
    if (this.settings?.user?.['userId']) {
      curUserId = this.settings.user['userId'];
    }
    if (!curUserId) {
      if (this.DEBUG_REQUIRED_FIELDS) {
        console.log(
          `[setControlRequired] No current user found for field: "${settingName}"`
        );
      }
      return;
    }

    const defaultSettings = this.defaultSettingsCache.get(curUserId) ?? null;
    if (!defaultSettings) {
      if (this.DEBUG_REQUIRED_FIELDS) {
        console.log(
          `[setControlRequired] No default settings found for field: "${settingName}"`
        );
      }
      return;
    }

    const lowerCaseDefaultSettings: { [key: string]: any } = {};
    Object.keys(defaultSettings).forEach(key => {
      lowerCaseDefaultSettings[key.toLowerCase()] = defaultSettings[key];
    });

    const setting = lowerCaseDefaultSettings[settingName.toLowerCase()];
    const isRequired = setting && setting.isRequired;

    if (this.DEBUG_REQUIRED_FIELDS) {
      console.log(`[setControlRequired] Field "${settingName}":`, {
        settingFound: !!setting,
        isRequired: isRequired,
        settingDetails: setting
      });
    }

    if (isRequired) {
      // Get existing validator function
      const existingValidator = control.validator;

      if (this.DEBUG_REQUIRED_FIELDS) {
        console.log(
          `[setControlRequired] Field "${settingName}" is REQUIRED. Existing validator:`,
          {
            hasExistingValidator: !!existingValidator,
            validatorType: existingValidator ? typeof existingValidator : 'none'
          }
        );
      }

      // Check if required validator is already present by testing validation
      const currentValue = control.value;
      control.setValue(null, { emitEvent: false });
      const alreadyHasRequired = control.hasError('required');
      control.setValue(currentValue, { emitEvent: false });

      if (this.DEBUG_REQUIRED_FIELDS) {
        console.log(
          `[setControlRequired] Field "${settingName}" validation check:`,
          {
            currentValue: currentValue,
            alreadyHasRequired: alreadyHasRequired,
            currentErrors: control.errors
          }
        );
      }

      if (!alreadyHasRequired) {
        // Add required validator while preserving existing validators
        if (existingValidator) {
          // Compose existing validator with required validator
          control.setValidators([existingValidator, Validators.required]);
          if (this.DEBUG_REQUIRED_FIELDS) {
            console.log(
              `[setControlRequired] Field "${settingName}": Added required validator while preserving existing validator`
            );
          }
        } else {
          control.setValidators([Validators.required]);
          if (this.DEBUG_REQUIRED_FIELDS) {
            console.log(
              `[setControlRequired] Field "${settingName}": Set required validator (no existing validators)`
            );
          }
        }
        control.updateValueAndValidity();

        if (this.DEBUG_REQUIRED_FIELDS) {
          console.log(
            `[setControlRequired] Field "${settingName}" after setting validators:`,
            {
              isValid: control.valid,
              errors: control.errors,
              value: control.value
            }
          );
        }
      } else {
        if (this.DEBUG_REQUIRED_FIELDS) {
          console.log(
            `[setControlRequired] Field "${settingName}": Required validator already present, skipping`
          );
        }
      }

      if (element) {
        const label = element.nativeElement.querySelector('label');
        if (label) {
          label.classList.add('ant-form-item-required');
          if (this.DEBUG_REQUIRED_FIELDS) {
            console.log(
              `[setControlRequired] Field "${settingName}": Added 'ant-form-item-required' class to label`
            );
          }
        } else {
          if (this.DEBUG_REQUIRED_FIELDS) {
            console.warn(
              `[setControlRequired] Field "${settingName}": Label element not found in provided element`
            );
          }
        }
      } else {
        if (this.DEBUG_REQUIRED_FIELDS) {
          console.log(
            `[setControlRequired] Field "${settingName}": No element provided for label styling`
          );
        }
      }
    } else {
      if (this.DEBUG_REQUIRED_FIELDS) {
        console.log(
          `[setControlRequired] Field "${settingName}": NOT marked as required in user default settings`
        );
      }
    }
  }

  setRequiredValidation(element?: ElementRef<HTMLDivElement>) {
    if (!element) return;

    const name = element.nativeElement?.getAttribute('data-control-name');
    if (!name) return;

    // Get current user ID and check cache synchronously
    let curUserId: string | null = null;
    if (this.settings?.user?.['userId']) {
      curUserId = this.settings.user['userId'];
    }
    if (!curUserId) {
      return;
    }

    const defaultSettings = this.defaultSettingsCache.get(curUserId) ?? null;
    if (!defaultSettings) {
      return;
    }

    const lowerCaseDefaultSettings: { [key: string]: any } = {};
    Object.keys(defaultSettings).forEach(key => {
      lowerCaseDefaultSettings[key.toLowerCase()] = defaultSettings[key];
    });

    if (
      lowerCaseDefaultSettings[name.toLowerCase()] &&
      lowerCaseDefaultSettings[name.toLowerCase()].isRequired
    ) {
      const label = element.nativeElement.querySelector('label');
      if (label) {
        label.classList.add('ant-form-item-required');
      }
    }
  }


  loadDefaultSettingsToCache(userId: string) {
    this.http.get('/api/userdefaultsettings/form', { uid: userId }).subscribe({
      next: (formData: any) => {
        // Backend now returns data directly in form structure
        // Convert to lowercase format for cache compatibility
        const lowerCaseFormValue: any = {};
        Object.keys(formData).forEach(key => {
          if (key === 'userName') {
            lowerCaseFormValue.username = formData[key];
          } else {
            // Convert field name to lowercase and preserve field structure
            const lowerKey = key.toLowerCase();
            lowerCaseFormValue[lowerKey] = {
              ...formData[key],
              // Add defaultItem alias for backward compatibility
              defaultItem: formData[key]?.default ?? null,
              // Add manageSelection alias for backward compatibility (using selectedItemIds)
              manageSelection: formData[key]?.selectedItemIds ?? []
            };
          }
        });

        this.updateDefaultSettingsCache(lowerCaseFormValue);
      },
      error: err => {
        console.warn('Failed to fetch user defaults:', err);
      }
    });
  }

  clearDefaultSettingsCache() {
    this.defaultSettingsCache.clear();
  }
  /**
   * Update the in-memory cache with default settings for a user.
   * @param data The default settings data, must include a 'username' property as the key.
   */
  updateDefaultSettingsCache(data: any) {
    // use the username as the key
    if (!data.username) {
      console.warn(
        'updateDefaultSettingsCache: No username found in data, skipping...'
      );
      return;
    }

    // Store in in-memory cache, keyed by username (userId)
    this.defaultSettingsCache.set(data.username, data);
  }
}
